import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableRow, Skeleton, Alert, TextField, Stack
} from '@mui/material';
import api from '../services/api';

const statusSteps = ['待付款', '已付款', '配送中', '已送達'];

const getActiveStep = (order) => {
  if (order.status === 'delivered') return 3;
  if (order.status === 'shipping') return 2;
  if (order.payment_status === 'paid') return 1;
  return 0;
};

const OrderStatus = () => {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 跨裝置查詢：非本人瀏覽器（無下單 session、未登入）時，需輸入訂單手機號驗證
  const [needPhone, setNeedPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [verifying, setVerifying] = useState(false);

  const fetchOrder = async (phoneParam) => {
    try {
      const response = await api.get(`/orders/${orderNo}`, {
        params: phoneParam ? { phone: phoneParam } : undefined
      });
      setOrder(response.data);
      setNeedPhone(false);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.needPhone) {
        setNeedPhone(true);
        setError(phoneParam ? '手機號碼與訂單不符，請重新確認' : null);
      } else {
        setError('訂單不存在或查詢失敗');
      }
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  const handleVerify = (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setVerifying(true);
    fetchOrder(phone.trim());
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Skeleton height={60} />
        <Skeleton height={100} />
        <Skeleton height={200} />
      </Box>
    );
  }

  // 跨裝置查詢：請本人輸入訂單手機號碼驗證
  if (needPhone) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', py: 6 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>訂單查詢</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            訂單編號：{orderNo}
            <br />
            為保護您的個人資料，請輸入此訂單的手機號碼以查看訂單內容。
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleVerify}>
            <Stack spacing={2}>
              <TextField
                label="訂單手機號碼"
                placeholder="09xxxxxxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                autoFocus
                inputProps={{ inputMode: 'numeric' }}
              />
              <Button type="submit" variant="contained" disabled={verifying || !phone.trim()}>
                {verifying ? '查詢中...' : '查詢訂單'}
              </Button>
              <Button onClick={() => navigate('/')}>回首頁</Button>
            </Stack>
          </form>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          回首頁
        </Button>
      </Box>
    );
  }

  const activeStep = getActiveStep(order);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        訂單查詢
      </Typography>

      {/* 訂單進度 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {statusSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* 待付款提醒 */}
      {order.payment_status === 'unpaid' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          請盡快完成付款！<br />
          繳費代碼：<strong>{order.payment_code || '產生中'}</strong><br />
          繳費期限：{order.payment_deadline
            ? new Date(order.payment_deadline).toLocaleString('zh-TW')
            : '7 天內'}
        </Alert>
      )}

      {/* 訂單資訊 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>訂單資訊</Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>訂單編號</TableCell>
              <TableCell>{order.order_no}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>訂單日期</TableCell>
              <TableCell>{new Date(order.created_at).toLocaleString('zh-TW')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>訂單金額</TableCell>
              <TableCell>
                <Typography color="error" fontWeight="bold">
                  ${order.total_amount}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>付款狀態</TableCell>
              <TableCell>
                {order.payment_status === 'paid' ? (
                  <Typography color="success.main">已付款</Typography>
                ) : (
                  <Typography color="warning.main">待付款</Typography>
                )}
              </TableCell>
            </TableRow>
            {order.paid_at && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>付款時間</TableCell>
                <TableCell>{new Date(order.paid_at).toLocaleString('zh-TW')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 取貨資訊 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>取貨資訊</Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>收件人</TableCell>
              <TableCell>{order.customer_name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>聯絡電話</TableCell>
              <TableCell>{order.customer_phone}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>取貨門市</TableCell>
              <TableCell>
                {order.store_type} - {order.store_name}<br />
                <Typography variant="body2" color="text.secondary">
                  {order.store_address}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* 商品明細 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>商品明細</Typography>
        {order.items?.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
            <Box>
              <Typography>{item.product_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                ${item.price} x {item.quantity}
              </Typography>
            </Box>
            <Typography fontWeight="bold">${item.subtotal}</Typography>
          </Box>
        ))}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">總計</Typography>
          <Typography variant="h6" color="error" fontWeight="bold">
            ${order.total_amount}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="contained" onClick={() => navigate('/')}>
          回首頁
        </Button>
      </Box>
    </Box>
  );
};

export default OrderStatus;
